import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { useRef } from 'react';
import { SpacedComma, TooltippedIcon } from '@mews-commander/ui';
import { LocalizedCurrencyValue, LocalizedText, useAppClockService } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { IconName, Typography, Body, Stack, StackItem } from '@optimus-web/core';
import { compactArray } from '@mews/fp';
import {
    getCurrentEnterpriseReservationBookerId,
    getCurrentEnterpriseReservationOwnerId,
} from '@mews-commander/legacy-entities/src/selectors/reservations';
import { getCustomerRelatedNotesString } from '@mews-commander/legacy-entities/src/ducks/accountNotes';
import { hasCustomerValidExternalCreditCard } from '@mews-commander/legacy-entities/src/ducks/paymentCards';
import {
    useCommanderSelector,
    useCommanderThunkDispatch,
    useGainsightCommanderEvents,
    useHoverEffect,
} from '@mews-commander/utils';
import { CustomerLink, CustomerAvatar } from '../../../../../components/commander-ui';
import { editInternal as editInternalAction } from '../../../actions/profile';
import { openPayments as openPaymentsAction } from '../../../actions';
import { getCustomerBalanceByCustomerId } from '../../../ducks/balances';
import { CustomerClassificationsLabel } from './ClassificationsLabelContainer';

interface CustomerBalanceLabelProps {
    customerId: string;
    reservationId: string;
    showBalance: boolean;
    guestVerificationPhotoSrc: string | null;
}

export const CustomerBalanceLabel: React.FC<CustomerBalanceLabelProps> = ({
    showBalance,
    customerId,
    reservationId,
    guestVerificationPhotoSrc,
}) => {
    const gainsightCommanderEvents = useGainsightCommanderEvents();
    const notesRef = useRef<HTMLSpanElement | null>(null);

    const dispatch = useCommanderThunkDispatch();
    const appClockService = useAppClockService();

    const notes = useCommanderSelector((state) =>
        getCustomerRelatedNotesString(state, { accountId: customerId, id: customerId })
    );
    const balance = useCommanderSelector((state) => getCustomerBalanceByCustomerId(state, { id: customerId }));
    const ownerId = useCommanderSelector((state) =>
        getCurrentEnterpriseReservationOwnerId(state, { id: reservationId })
    );
    const bookerId = useCommanderSelector((state) =>
        getCurrentEnterpriseReservationBookerId(state, { id: reservationId })
    );
    const hasValidExternalCreditCard = useCommanderSelector((state) =>
        hasCustomerValidExternalCreditCard(state, {
            accountId: customerId,
            appClockService,
        })
    );

    const editInternals = () =>
        dispatch(
            editInternalAction({
                accountId: customerId,
            })
        );

    const openPayments = () => dispatch(openPaymentsAction({ accountId: customerId }));

    useHoverEffect((hovered) => {
        if (hovered) {
            gainsightCommanderEvents.onElementHover({ context: 'CustomerNote' });
        }
    }, notesRef);

    return (
        <Stack distribution="space-between" spacing="50" data-test-smart-detail-customer-label={customerId}>
            <StackItem minWidth="0">
                <Stack spacing="100" itemAlignment="center">
                    <CustomerAvatar accountId={customerId} guestVerificationPhotoSrc={guestVerificationPhotoSrc} />
                    <StackItem minWidth="0">
                        <Stack vertical spacing="50" alignment="center">
                            <Typography textStyle="bodyMedium" as="div">
                                <Stack spacing="50" itemAlignment="center" wrap>
                                    {notes !== null && notes !== undefined && (
                                        <TooltippedIcon
                                            ref={notesRef}
                                            value={IconName.Notes}
                                            tooltip={notes}
                                            onClick={editInternals}
                                            size="small"
                                        />
                                    )}
                                    <CustomerLink id={customerId} />
                                    <Typography textStyle="bodySmallStrong" variant="secondary" as="div">
                                        {pipe(
                                            compactArray([
                                                customerId === ownerId ? (
                                                    <LocalizedText textKey={TextKey.Owner} />
                                                ) : null,
                                                customerId === bookerId ? (
                                                    <LocalizedText textKey={TextKey.Booker} />
                                                ) : null,
                                            ]),
                                            array.intersperse<React.ReactNode>(SpacedComma)
                                        )}
                                    </Typography>
                                </Stack>
                            </Typography>
                            <CustomerClassificationsLabel
                                customerId={customerId}
                                handleClassificationClick={editInternals}
                            />
                        </Stack>
                    </StackItem>
                </Stack>
            </StackItem>
            <Stack itemAlignment="center" spacing="100" alignment="end">
                {hasValidExternalCreditCard && (
                    <TooltippedIcon
                        size="small"
                        value={IconName.PaymentCard}
                        tooltip={TextKey.PaymentCard}
                        onClick={openPayments}
                    />
                )}
                {showBalance && balance && (
                    <Body size="small" whiteSpace="nowrap">
                        <LocalizedCurrencyValue value={balance.balance} />
                    </Body>
                )}
            </Stack>
        </Stack>
    );
};
